import React from "react";
import { useNavigate } from "react-router-dom";
import { Lock, Sparkles, ShieldAlert, AlertTriangle, TrendingDown, TrendingUp, BarChart3, Brain } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTranslation } from "@/lib/i18n";

/**
 * AlphaSignals — premium "institutional intelligence" panel.
 *
 * Each signal shows a short headline + a *visible* preview teaser.
 * For free users the rest of the reasoning is rendered blurred (CSS filter)
 * with a subtle Pro lock cue. For Pro users the continuation is rendered as
 * normal text. This creates curiosity ("show me the rest") without an
 * aggressive paywall overlay.
 */
// Phase 7.UI.10 — `neutral` severity (used by sector_percentile and similar
// info signals) is now the brand blue family. Reserves grey purely for
// "no signal" states elsewhere. Matches the InstitutionalSnapshot neutral.
const SEVERITY_TOKENS = {
  high:     { fg: "#E5484D", bg: "rgba(229,72,77,0.10)",  border: "rgba(229,72,77,0.32)",  icon: ShieldAlert },
  medium:   { fg: "#E0B341", bg: "rgba(224,179,65,0.10)", border: "rgba(224,179,65,0.32)", icon: AlertTriangle },
  positive: { fg: "#3FAE89", bg: "rgba(63,174,137,0.10)", border: "rgba(63,174,137,0.32)", icon: TrendingUp },
  neutral:  { fg: "#5B8DC4", bg: "rgba(49,130,206,0.10)", border: "rgba(49,130,206,0.32)", icon: BarChart3 },
};

function signalIcon(id, fallbackIcon) {
  if (id === "earnings_quality") return TrendingDown;
  if (id === "value_trap") return ShieldAlert;
  if (id === "balance_sheet") return ShieldAlert;
  if (id === "multiple_compression") return TrendingDown;
  if (id === "low_confidence") return AlertTriangle;
  if (id === "growth_durability") return TrendingUp;
  if (id === "quality_value_alignment") return Sparkles;
  if (id === "sector_percentile") return BarChart3;
  return fallbackIcon || Brain;
}

export default function AlphaSignals({ signals = [], aiFull = false, ticker, className = "" }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  if (!signals.length) return null;
  return (
    <div
      data-testid="alpha-signals-panel"
      className={`relative overflow-hidden rounded-2xl border border-border/70 bg-card/60 p-5 sm:p-6 ${className}`}
    >
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: "radial-gradient(900px 240px at 100% 0%, rgba(49,130,206,0.18), transparent 70%)" }}
      />

      {/* Header */}
      <div className="relative flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2.5">
          <span className="inline-flex w-9 h-9 items-center justify-center rounded-lg bg-secondary/60 border border-border/70">
            <Brain className="w-4 h-4 text-[#3182CE]" />
          </span>
          <div>
            <div className="flex items-center gap-2">
              <div className="font-display text-lg leading-tight">{t("signals.title")}</div>
              {!aiFull ? (
                <span
                  className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
                  style={{ color: "#3182CE", background: "rgba(49,130,206,0.10)", border: "1px solid rgba(49,130,206,0.32)" }}
                  data-testid="alpha-signals-pro-tag"
                >
                  <Lock className="w-2.5 h-2.5" /> Pro
                </span>
              ) : (
                <span
                  className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
                  style={{ color: "#3FAE89", background: "rgba(63,174,137,0.10)", border: "1px solid rgba(63,174,137,0.32)" }}
                >
                  <Sparkles className="w-2.5 h-2.5" /> {t("signals.full_access")}
                </span>
              )}
            </div>
            <div className="text-xs text-muted-foreground mt-0.5">
              {t("signals.subtitle", { ticker })}
            </div>
          </div>
        </div>
      </div>

      {/* Signals list */}
      <div className="relative mt-4 space-y-2.5">
        {signals.map((s) => {
          const tok = SEVERITY_TOKENS[s.severity] || SEVERITY_TOKENS.neutral;
          const Icon = signalIcon(s.id, tok.icon);
          // Phase 7.UI.11 — frontend-side translation of backend-issued
          // signal titles/previews via dictionary lookup. Phase 7.UI.13b —
          // preview/locked templates support {param} interpolation via
          // `s.params` emitted by the deterministic backend generator.
          // Falls back gracefully to the backend text for any signal id
          // without a translation key (e.g., older cached payloads or
          // pre-deployment cached rows) AND for any locale where the
          // translated template contains unresolved {placeholders}
          // because the backend cache row predates the params emission.
          const titleKey = `signals.items.${s.id}.title`;
          const previewKey = `signals.items.${s.id}.preview`;
          const lockedKey = `signals.items.${s.id}.locked`;
          const params = s.params || {};
          const tx = t(titleKey);
          const px = t(previewKey, params);
          const lx = t(lockedKey, params);
          const hasPlaceholder = (str) => typeof str === "string" && /\{[a-z_]+\}/i.test(str);
          const title = tx === titleKey ? s.title : tx;
          const preview = (px === previewKey || hasPlaceholder(px)) ? s.preview : px;
          const locked = (lx === lockedKey || hasPlaceholder(lx)) ? s.locked : lx;
          return (
            <div
              key={s.id}
              data-testid={`alpha-signal-${s.id}`}
              className="rounded-xl border border-border/60 bg-card/40 p-3 sm:p-3.5"
              style={{ borderColor: tok.border }}
            >
              <div className="flex items-start gap-2.5">
                <span
                  className="mt-0.5 inline-flex w-7 h-7 items-center justify-center rounded-md shrink-0"
                  style={{ color: tok.fg, background: tok.bg, border: `1px solid ${tok.border}` }}
                >
                  <Icon className="w-3.5 h-3.5" />
                </span>
                <div className="min-w-0 flex-1">
                  <div
                    className="font-medium text-sm leading-snug"
                    style={{ color: tok.fg }}
                    data-testid={`alpha-signal-${s.id}-title`}
                  >
                    {title}
                  </div>
                  <div className="mt-1 text-[13px] leading-relaxed text-foreground/85">
                    <span data-testid={`alpha-signal-${s.id}-preview`}>{preview}</span>{" "}
                    {aiFull ? (
                      <span data-testid={`alpha-signal-${s.id}-locked`}>{locked}</span>
                    ) : (
                      <span
                        className="select-none"
                        style={{ filter: "blur(5px)" }}
                        aria-hidden
                        data-testid={`alpha-signal-${s.id}-locked-blurred`}
                      >
                        {locked}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer CTA (only for free users) */}
      {!aiFull ? (
        <div className="relative mt-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-xl border border-dashed border-border/70 bg-card/30 p-3.5">
          <div className="flex items-start gap-2.5">
            <Sparkles className="w-4 h-4 text-[#3182CE] mt-0.5" />
            <div>
              <div className="text-sm font-medium">{t("signals.cta_title")}</div>
              <div className="text-xs text-muted-foreground">{t("signals.cta_subtitle")}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate("/pricing")} data-testid="alpha-see-plans">{t("signals.cta_see_plans")}</Button>
            <Button size="sm" onClick={() => navigate("/billing")} data-testid="alpha-upgrade-cta">
              <Sparkles className="w-3.5 h-3.5 mr-1" /> {t("signals.cta_upgrade")}
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
